import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { createLead } from "../lib/api";
import { CITIES } from "../lib/site";

const BILLS = ["Below ₹1,500", "₹1,500 – ₹3,000", "₹3,000 – ₹6,000", "₹6,000 – ₹12,000", "Above ₹12,000"];

export default function LeadForm({ sourcePage = "Homepage", defaultCity = "", dark = false }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    monthly_bill: "", property_type: "Residential", city: defaultCity, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, source_page: sourcePage, city: form.city || defaultCity });
      setDone(true);
      toast.success("Thanks! Our solar advisor will call you shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `text-xs uppercase tracking-[0.2em] ${dark ? "text-cream/50" : "text-moss"}`;
  const inputCls = `field-underline w-full font-body ${dark ? "text-cream placeholder:text-cream/30" : "text-ink placeholder:text-ink/30"}`;

  if (done) {
    return (
      <motion.div data-testid="lead-success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className={`flex flex-col items-center justify-center gap-4 rounded-3xl p-12 text-center ${dark ? "bg-white/5" : "bg-sand"}`}>
        <CheckCircle2 className="h-14 w-14 text-gold" />
        <h3 className={`font-heading text-2xl font-bold ${dark ? "text-cream" : "text-forest"}`}>Request received</h3>
        <p className={dark ? "text-cream/60" : "text-moss"}>A Synergy Solar advisor will reach out within one business day with your free rooftop assessment.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>Full name*</label>
        <input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>Phone*</label>
        <input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className={inputCls} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>Email*</label>
        <input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>City</label>
        <select data-testid="lead-city" value={form.city} onChange={set("city")} className={`${inputCls} cursor-pointer`}>
          <option value="">Select city</option>
          {CITIES.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <label className={labelCls}>Installation address</label>
        <input data-testid="lead-address" value={form.address} onChange={set("address")} placeholder="House / building, sector, city" className={inputCls} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>Avg. monthly electricity bill</label>
        <select data-testid="lead-bill" value={form.monthly_bill} onChange={set("monthly_bill")} className={`${inputCls} cursor-pointer`}>
          <option value="">Select range</option>
          {BILLS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className={labelCls}>Property type</label>
        <select data-testid="lead-type" value={form.property_type} onChange={set("property_type")} className={`${inputCls} cursor-pointer`}>
          <option>Residential</option>
          <option>Commercial / Industrial</option>
          <option>Housing Society</option>
        </select>
      </div>
      <button data-testid="lead-submit" type="submit" disabled={loading}
        className="group mt-2 flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-obsidian transition-transform duration-300 hover:scale-[0.98] hover:bg-gold-hover disabled:opacity-60 sm:col-span-2">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Get my free quote <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
}
